import React, { useState, useEffect } from 'preact/compat';
import { useFormikContext, Field } from 'formik';
import { SelfServiceFormValues } from '../../../lib/enquiry';
import { Address, findAddress, formatAddress } from '../../../lib/address';
import AddressFields from './address';
import FormLabel from '../label';
import { useSelfServiceFormContext } from './context';
import FormError from '../error';

const emptyAddress: Address = {
    line1: '',
    line2: '',
    city: '',
    county: '',
    postcode: '',
};

export default function AddressSearchFields() {
    const [results, setResults] = useState<Address[] | null>(null);
    const [searching, setSearching] = useState(false);
    const {
        setFieldValue,
        values: {
            address: {
                manual,
                search,
            },
        },
    } = useFormikContext<SelfServiceFormValues>();
    const {
        classNames: {
            button: classNames,
        },
    } = useSelfServiceFormContext();

    function setAddress(address: Address) {
        setFieldValue('address.line1', address.line1);
        setFieldValue('address.line2', address.line2);
        setFieldValue('address.city', address.city);
        setFieldValue('address.county', address.county);
        setFieldValue('address.postcode', address.postcode);
    }

    async function handleClick() {
        if (search === '') {
            return;
        }

        setSearching(true);
        setResults(null);
        setAddress(emptyAddress);

        try {
            setResults(await findAddress(search));
        } catch (error) {
            setSearching(false);
            setFieldValue('address.manual', true);
            setFieldValue('address.postcode', search);

            console.error(error);
        }
    }

    return (
        <div className='form__grid-item  form__grid-item--wide  form__grid'>
            <div className='form__grid-item  form__grid'>
                <div className='form__grid-item  form__grid-item--wide  form__field'>
                    <div className='form__grid-item  form__field'>
                        <FormLabel htmlFor='postcodeSearch' required={true}>Postcode</FormLabel>
                        <Field name='address.search' id='postcodeSearch' className='input  input--text' />
                        <FormError name='address.searchComplete' />
                    </div>
                </div>
                <div className='form__grid-item  form__grid-item--wide'>
                    <button type='button' onClick={handleClick} className={classNames.findAddress}>
                        Find address
                    </button>
                </div>
            </div>
            {searching && (
                <div className='form__grid-item  form__grid-item--wide'>
                    <AddressSearchResults addresses={results} setAddress={setAddress} />
                </div>
            )}
            {(!searching && manual) && (
                <div className='form__grid-item  form__grid-item--wide'>
                    <AddressFields />
                </div>
            )}
        </div>
    );
}

function AddressSearchResults({ addresses, setAddress }: AddressSearchResultsProps) {
    const [showFields, setShowFields] = useState(false);
    const {
        setFieldValue,
        values: {
            address: {
                manual,
            },
        },
    } = useFormikContext<SelfServiceFormValues>();

    useEffect(() => {
        setShowFields(false);
    }, [addresses]);

    if (addresses === null) {
        return (
            <div className='form__field'>
                <span className='form__label'>Finding addresses…</span>
                <select disabled={true} className='input  input--select' />
            </div>
        );
    }

    function handleChange(event: Event) {
        if (addresses === null) {
            return;
        }

        const select = event.target as HTMLSelectElement;
        const address = addresses[select.selectedIndex - 1];

        if (address === undefined) {
            setAddress(emptyAddress);
            setShowFields(false);
            setFieldValue('address.searchComplete', false);
        } else {
            setAddress(address);
            setShowFields(true);
            setFieldValue('address.searchComplete', true);
        }
    }

    return (
        <div className='form__grid'>
            <div className='form__grid-item  form__grid-item--wide  form__field'>
                <FormLabel htmlFor='searchResults'>Select your address</FormLabel>
                <select onChange={handleChange} id='searchResults' disabled={manual} className='input  input--select'>
                    <option />
                    {addresses.map((address, i) => (
                        <option key={i}>
                            {formatAddress(address)}
                        </option>
                    ))}
                </select>
            </div>
            {!showFields && (
                <div className='form__grid-item  form__grid-item--wide'>
                    <div className='form__grid'>
                        <div className='form__grid-item  form__grid-item--wide'>
                            <label className='toggle-container toggle-container--center'>
                                <span className='toggle-container__input'>
                                    <Field type='checkbox' name='address.manual' className='toggle  toggle--checkbox' />
                                </span>
                                <span className='toggle-container__label'>
                                    Enter address manually
                                </span>
                            </label>
                        </div>
                        {manual && (
                            <div className='form__grid-item  form__grid-item--wide'>
                                <AddressFields />
                            </div>
                        )}
                    </div>
                </div>
            )}
            {(showFields && !manual) && (
                <div className='form__grid-item  form__grid-item--wide'>
                    <AddressFields />
                </div>
            )}
        </div>
    );
}

type AddressSearchResultsProps = {
    addresses: Address[] | null,
    setAddress: (address: Address) => void,
};
